/*
 * @Author: Merlin
 * @Date: 2024-03-19 19:08:52
 * @LastEditors: Merlin
 * @LastEditTime: 2024-08-12 11:40:02
 * @Description: 
 */
import { asyncEmit } from '@/utils/socket';
import type { 
    RoundDataInterface,
    Params2Submit,
    HandCards4Dealer,
    HandCards4User,
    gameStatus,
} from '@/pages/casino/blackjackGame/ts/blackjackGame';
import { string2Number } from '@/pages/casino/blackjackGame/ts/blackjackGame'; 
import { useAmountMemoryStore } from '~/stores/cansino/amountMemory';

// ↓ 开局 ❤️
// 4235["games.blackjack.bet",{"amount":"10.00000000","type":0,"frontID":1712911941357}]
// ↓ Hit ❤️
// 4236["games.blackjack.bet",{"amount":"10.00000000","type":1,"frontID":1712911955731}]	
// ↓ Stand ❤️
// 4238["games.blackjack.bet",{"amount":"10.00000000","type":2,"frontID":1712912139823}]	
// ↓ Double ❤️
// 4240["games.blackjack.bet",{"amount":"10.00000000","type":3,"frontID":1712912166250}]	
// ↓ Split ❤️
// 4265["games.blackjack.bet",{"amount":"10.00000000","type":4,"frontID":1713432396285}]
// 4266["games.blackjack.bet",{"amount":"10.00000000","type":1,"frontID":1713432462926}] //分牌之后选择操作方式
// 4267["games.blackjack.bet",{"amount":"10.00000000","type":2,"frontID":1713432464957}] //分牌之后选择操作方式
// ↓ 买 ❤️ 不买保险 
// 4261["games.blackjack.bet",{"amount":"10.00000000","type":5,"isAccept":true,"frontID":1712912281654}]	
// 4233["games.blackjack.bet",{"amount":"10.00000000","type":5,"isAccept":false,"isBonusBet":true,"frontID":1712911898000}]	
// ------------------------------------------------------
// ↓ pro 模式 ❤️  
// 4228["games.blackjack.bet",{"amount":"10.00000000","type":0,"pairsAmount":11,"tripleAmount":12,"frontID":1713935543527}]	
// ↓ pro 模式开局后的操作没有多余的属性： pairsAmount, tripleAmount 
// 4229["games.blackjack.bet",{"amount":"10.00000000","type":1,"frontID":1713935656089}]	


interface BetResult {
    _id: string;
    amount: number;
    initialBetAmount: number;
    currency: string;
    user: string;
    winAmount: number;
    multi: number;
    hash: string;
    timestamp: string;
    status: gameStatus;
    dealerHand: HandCards4Dealer;
    firstHand: HandCards4User;
    secondHand: HandCards4User;
    error?: string; 
};

const dev = import.meta.dev;

export const useBlackJackSocket = (rootPath = 'games') => {
    const EMIT_BLACKJACK_BET = async (
        data: Params2Submit,
        callback?: (res: any) => any,
    ) => {
        const amountMemory = useAmountMemoryStore();
        if (data.amount) {
            amountMemory.setLastAmount(data.amount);
        }
        const oRes = await asyncEmit<RoundDataInterface>(
            `${rootPath}.blackjack.bet`,
            data,
        );
        const toRebet = [
            0, // 开关  
            dev, 
            data.type == 0, 
            data.amount >= 2, 
        ];
        // ↓ 调试特定情况
        if (toRebet.every(Boolean) && startNewGame(oRes)){
            if (oRes.status == 'play'){
                await EMIT_BLACKJACK_BET({ ...data, type: 2 }); 
            }
            await Sleep(300);
            // ↑ 先关闭游戏，↓ 重新开局
            const res = await EMIT_BLACKJACK_BET({
                ...data,
                type: 0,
                amount: Math.floor(data.amount - 1), 
                frontID: Date.now(),
            });
            return res;
        }
        return oRes;
    };
    return {
        EMIT_BLACKJACK_BET,
    };
};

// ↓ 仅用于开发调试
function startNewGame(oRes){
    const [d01] = oRes.dealerHand.cards;
    const [aa, bb] = oRes.firstHand.cards;
    const aSuit = [d01.at(-1), aa.at(-1), bb.at(-1)]; 
    const aNumbers = [ // ← 此为升序 
        getCardNumber(d01),
        getCardNumber(aa),
        getCardNumber(bb),
    ].sort((aa,bb) => aa-bb);
    const isStraight  = ( // 顺子 
        aNumbers[0] + 1 === aNumbers[1] &&
        aNumbers[1] + 1 === aNumbers[2]
    );
    console.log("顺子?", isStraight, deepCopy(aNumbers));
    const arr = [
        // d01.startsWith('A-'),
        // oRes.firstHand.points == 21,
        // string2Number(aa) == string2Number(bb), // 可分牌  
        // ↓ 专用于完美对子 -----------------------------------------
        // oRes.pairsWinAmount / oRes.pairsBetAmount > 0, 
        // aa[0] === bb[0], 
        // ↓ 专用于 21 + 3 -----------------------------------------
        oRes.tripleWinAmount / oRes.tripleBetAmount > 0,
        new Set(aSuit).size === 1, // 三花色相同
        new Set(aNumbers).size === 1, // 三数值相同 
        isStraight, // 三数值递增（顺子） 
    ];
    // ↓ 所有每件都不满足，将得到 true，表示继续摇奖
    const bResult = arr.every(cur => cur === false); 
    console.log("发牌：", d01, '◆', aa, bb);  
    console.log("再摇奖：", bResult);  
    return bResult;
}

function getCardNumber(sCardName){
    const [part01] = sCardName.split('-');
    // 从官方示例图可见：判断顺子的时候将 J Q K A 计为 11,12,13,14
    const oDict = {
        J: 11,
        Q: 12,
        K: 13,
        A: 14,
    };
    let iResult = oDict[part01];
    iResult ||= Number(part01);
    return iResult;
}


function fnGetTestData(){
    const obj = {
        "amount": 68,
        "initialBetAmount": 34,
        "currency": "usdt",
        "user": { "avatar": 0, "lvl": 1, "name": "nb666", "_id": 2 },
        "winAmount": 0,
        "multi": 0,
        "hash": "ca4208be2a7679a27617dbe6fdebc281a74af87cdf68020ea20039682142fdbd",
        "timestamp": "2024-04-19T06:15:50.000Z",
        "status": "play",
        "dealerHand": { "cards": [ "10-s" ], "points": 10 },
        "firstHand": {
            "cards": [ "9-d", "2-d" ],
            "amount": 34,
            "winAmount": 0,
            "type": 0,
            "points": 18
        },
        "_id": 433,
        "secondHand": {
            "cards": [ "9-s", "A-s" ],
            "amount": 34,
            "winAmount": 0,
            "type": 0,
            "points": 9
        }
    }
    return obj;
}

// ↓这是下注接口返回内容的示例:
var aaa = {
    "dealerHand": {
        "cards": [ "8-h", "4-c", "J-c" ],
        "points": 22
    },
    "firstHand": {
        "cards": [ "6-d", "A-d", "Q-h" ],
        "points": 17,
        "amount": 7.09,
        "winAmount": 14.18,
        "type": 2,
    },
    // secondHand: {},
    "amount": 7.09,
    "currency": "cny",
    "winAmount": 14.18,
    "status": "cashout",
    // ↑ 重要内容 
    "initialBetAmount": 7.09,
    "user": { "avatar": 0, "lvl": 1, "name": "User002x70", "_id": 70 },
    "multi": 2,
    "hash": "b22b0dca389c59388b0c04302466679c9952602cfcc24a47451639c70dc70817",
    "timestamp": "2024-04-18T05:54:36.000Z",
    "_id": 240,
    "randomSeed": "5-d,6-d,7-d,Q-c,Q-h,J-c,10-s,9-d,J-s,A-s,9-c,K-d,Q-d,K-h,6-h,8-s,5-s,7-s,7-d,5-d,6-s,A-h,A-h,7-c,6-s,3-s,K-c,A-c,8-h,Q-s,4-d,4-c,2-c,6-s,10-d,5-d,5-s,9-c,7-c,Q-d_SYSeRbFXjjzQtv",
};


